import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-newsprint flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white border border-gray-300 rounded-2xl p-8 fade-up shadow-[6px_6px_0_0_rgba(124,58,237,0.08)]">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500 mb-2">
          Account · Recovery
        </div>
        <h1 className="font-display text-3xl tracking-[-0.02em] font-medium leading-tight">
          Forgot your password?
        </h1>

        {!sent ? (
          <>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">
              Enter the email you used to sign up. We&apos;ll send a one-time link to set a new password.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-gray-500">Email</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full mt-1 border border-gray-300 focus:border-[#7C3AED] focus:ring-1 focus:ring-[#7C3AED] outline-none px-3 py-2.5 text-sm bg-white rounded-lg"
                  placeholder="you@studio.com"
                />
              </div>
              {err && (
                <div data-testid="forgot-error" className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-lg">
                  {err}
                </div>
              )}
              <button
                data-testid="forgot-submit"
                disabled={busy}
                className="w-full bg-black text-white hover:bg-gray-800 disabled:opacity-60 rounded-lg py-3 font-medium inline-flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Send reset link
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-sent" className="mt-5">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-gradient text-white">
              <Mail size={18} strokeWidth={1.8} />
            </div>
            <h2 className="font-display text-xl font-medium tracking-tight mt-4">Check your inbox.</h2>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              If <span className="font-medium text-gray-900">{email}</span> is on file with us, we just sent a reset link.
              It expires in 1 hour and can only be used once.
            </p>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-dashed border-gray-200 text-sm text-gray-600 space-y-2">
          <div>
            Remembered it? <Link to="/login" className="text-[#7C3AED] hover:underline" data-testid="forgot-back-login">Back to sign in</Link>
          </div>
          <div className="text-xs text-gray-500 leading-relaxed">
            Forgot which email you used? Reply to <span className="font-medium text-gray-700">brief@fountstories.com</span> from any address you might&apos;ve signed up with — we&apos;ll help you recover.
          </div>
        </div>
      </div>
    </div>
  );
}
